import { HttpClient } from 'aurelia-fetch-client';
import { autoinject } from 'aurelia-framework';
import 'whatwg-fetch';
import config from '../config/config';
import { ApiResponse } from '../models/api-response';
import { CreateAutopayRequest, UpdateAutopayRequest } from '../models/hpfApi';
import { ServiceResponse } from '../models/service-response';
import { State } from './global-state';

@autoinject
export class HPFServiceClient {
	base: string;
	queryParameters: any;

	constructor(
		private globalState: State,
		private http: HttpClient,
	) {
		this.base = config.hpfServiceUrl;
		this.queryParameters = {};
		location.search.substr(1).split('&').forEach(item => {
			const index = decodeURI(item.split('=')[0].toLowerCase());
			const value = decodeURI(item.split('=')[1]).replace('undefined', '');
			this.queryParameters[index] = value;
		});
	}

	// ---------------------------------------------------------------------------------
	// Autopay (todo: move to separate service)
	// ---------------------------------------------------------------------------------

	getAutopayByAccount(customerAccountKey: string): Promise<ApiResponse> {
		const url = `/customerAccounts/${customerAccountKey}/autopay`;
		return this.get(url);
	}

	getAutopayByKey(autopayKey: string): Promise<ApiResponse> {
		const url = `/autopay/${autopayKey}`;
		return this.get(url);
	}

	getPortfolioAutopaySettings(portfolioKey: number): Promise<ApiResponse> {
		const url = `/portfolio/${portfolioKey}/autopaySettings`;
		return this.get(url);
	}

	createAutopay(request: CreateAutopayRequest): Promise<ApiResponse> {
		const url = `/autopay`;
		return this.post(url, request);
	}

	updateAutopay(autopayKey: string, request: UpdateAutopayRequest): Promise<ApiResponse> {
		const url = `/autopay/${autopayKey}`;
		return this.put(url, request);
	}

	deleteAutopay(autopayKey: string): Promise<ApiResponse> {
		const url = `/autopay/${autopayKey}`;
		return this.delete(url, {});
	}

	// ---------------------------------------------------------------------------------
	// Buckets (todo: move to separate service)
	// ---------------------------------------------------------------------------------

	getBucket(bucketKey: number): Promise<ApiResponse> {
		const url = `/bucket/${bucketKey}`;
		return this.get(url);
	}

	getBuckets(): Promise<ApiResponse> {
		const url = `/bucket?isActiveOnly=true`;
		return this.get(url);
	}

	// ---------------------------------------------------------------------------------
	// Internal http functions
	// ---------------------------------------------------------------------------------

	updateJwtToken(): Promise<any> {
		const url = `/token`;
		return this.put(url, null)
			.then(r => {
				if (r.isSuccessful) {
					this.globalState.token = r.value;
				}
			});
	}

	get(url: string): Promise<ApiResponse> {
		let init = {
			method: 'get',
			headers: new Headers({
				'Content-Type': 'application/json'
			})
		};

		return this.http.fetch(`${this.base}${url}`, init)
			.then(r => r.json());

	}

	put(url: any, content: any): Promise<ApiResponse> {
		let request = JSON.stringify(content);

		let init = {
			method: 'put',
			headers: new Headers({
				'Content-Type': 'application/json'
			}),
			body: request
		};

		return this.http.fetch(`${this.base}${url}`, init)
			.then(r => r.json());
	}

	post(url: any, content?: any) {
		let request = JSON.stringify(content);

		let init = {
			method: 'post',
			headers: new Headers({
				'Content-Type': 'application/json'
			}),
			body: request
		};

		return this.http.fetch(`${this.base}${url}`, init)
			.then(response => {
				if (response.status === 204) {
					return '';
				} else {
					return response.json();
				}
			})
			.then(response => {
				return response;
			});
	}

	delete(url: string, content: any): Promise<ApiResponse> {
		let request = JSON.stringify(content);

		let init = {
			method: 'delete',
			headers: new Headers({
				'Content-Type': 'application/json'
			}),
			body: request
		};

		return new Promise<ApiResponse>((resolve) => {
			return this.http
				.fetch(`${this.base}${url}`, init)
				.then(response => {
					response.json()
						.then((responseJson: ApiResponse) => {
							resolve(this.createApiResponseFromHttpResponse(responseJson));
						})
						.catch(error => {
							resolve(new ApiResponse({
								isSuccessful: false,
								errors: ['API Delete Failed.']
							}));
						});
				})
				.catch(error => {
					resolve(new ApiResponse({
						isSuccessful: false,
						errors: ['API Delete Failed.']
					}));
				});
		});
	}

	// explicitly map the http json response into an api response in order to overcome
	// serialization issues that come with implicit serialization (e.g. methods undefined)
	private createApiResponseFromHttpResponse(responseJson: ApiResponse): ApiResponse {
		return new ApiResponse({
			isSuccessful: responseJson.isSuccessful,
			resultCode: responseJson.resultCode,
			value: responseJson.value,
			logMessages: responseJson.logMessages,
			errors: responseJson.errors
		});
	}

	protected success(successMessage: string = '', value: any = null): Promise<ServiceResponse> {
		return new Promise<ServiceResponse>((resolve) => {
			resolve(new ServiceResponse({ isSuccessful: true, resultMessage: successMessage, value }));
		});
	}

	protected error(errorMessage: string = ''): Promise<ServiceResponse> {
		return new Promise<ServiceResponse>((resolve) => {
			resolve(new ServiceResponse({ isSuccessful: false, resultMessage: errorMessage, value: null }));
		});
	}

}
